import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '../components/ui/dialog';
import { Plus, Trash2, Save, Download, Loader2, Copy, Power, Receipt, FileText, AlertTriangle, Gauge } from 'lucide-react';
import { brandColors } from '../components/Brand';

const fmtINR = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');
const INDIAN_STATES = ['Maharashtra', 'Karnataka', 'Delhi', 'Tamil Nadu', 'Gujarat', 'Telangana',
  'West Bengal', 'Uttar Pradesh', 'Rajasthan', 'Kerala', 'Haryana', 'Punjab', 'Madhya Pradesh'];

const Field = ({ label, children }) => (
  <div className="space-y-1"><Label className="text-xs text-slate-500">{label}</Label>{children}</div>
);

const LimitStat = ({ label, value }) => (
  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
    <p className="text-xs text-slate-500">{label}</p>
    <p className="font-semibold text-slate-900">{value}</p>
  </div>
);

const RevenueAdmin = () => {
  const { getAuthHeaders, user } = useAuth();
  const h = getAuthHeaders();
  const isSuper = user?.roles?.includes('SuperAdmin');
  const [data, setData] = useState(null);
  const _params = new URLSearchParams(window.location.search);
  const [tab, setTab] = useState(_params.get('tab') || 'plans');
  const initialLimitTenant = _params.get('limitTenant') || '';

  const reload = useCallback(async () => {
    try {
      const [plans, features, actions, ocr, subs, invoices, discounts, tenants, settings, billing] = await Promise.all([
        api.getPlans(h), api.getFeatureMatrix(h), api.getActionMaster(h), api.getOcrRates(h),
        api.getAllSubscriptions(h), api.getInvoices(h), api.getDiscounts(h), api.listCompanies(h),
        api.getRevenueSettings(h), api.getBilling(h),
      ]);
      const seen = {}; const uniq = [];
      (tenants.data || []).forEach((c) => { if (!seen[c.code]) { seen[c.code] = 1; uniq.push(c); } });
      setData({
        plans: plans.data, features: features.data, actions: actions.data, ocr: ocr.data,
        subs: subs.data, invoices: invoices.data, discounts: discounts.data, tenants: uniq,
        settings: settings.data, billing: billing.data,
      });
    } catch (e) { toast.error('Failed to load revenue admin data'); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  if (!isSuper) return <div className="p-8 text-slate-500">SuperAdmin access required.</div>;
  if (!data) return <div className="flex items-center justify-center py-20 text-slate-500"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…</div>;

  return (
    <div data-testid="revenue-admin" className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold" style={{ color: brandColors.primary }}>Revenue &amp; Subscription Engine™</h1>
        <p className="text-slate-600 mt-1">Master control panel — plans, pricing, subscriptions, billing &amp; GST invoicing.</p>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="plans" data-testid="tab-plans">Plans</TabsTrigger>
          <TabsTrigger value="features" data-testid="tab-feature-matrix">Feature Matrix</TabsTrigger>
          <TabsTrigger value="credits" data-testid="tab-ai-credit-master">AI Credit Master</TabsTrigger>
          <TabsTrigger value="ocr" data-testid="tab-ocr-rates">OCR Rate Master</TabsTrigger>
          <TabsTrigger value="packs" data-testid="tab-industry-packs">Industry Packs</TabsTrigger>
          <TabsTrigger value="subs" data-testid="tab-subscriptions">Subscriptions</TabsTrigger>
          <TabsTrigger value="billing" data-testid="tab-billing">Billing</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="tab-invoices">Invoices</TabsTrigger>
          <TabsTrigger value="discounts" data-testid="tab-discounts">Discounts</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-system-settings">System Settings</TabsTrigger>
        </TabsList>
        <TabsContent value="plans"><PlansTab data={data} h={h} reload={reload} /></TabsContent>
        <TabsContent value="features"><FeaturesTab data={data} h={h} reload={reload} /></TabsContent>
        <TabsContent value="credits"><CreditMasterTab data={data} h={h} reload={reload} /></TabsContent>
        <TabsContent value="ocr"><OcrTab data={data} h={h} reload={reload} /></TabsContent>
        <TabsContent value="packs"><PacksTab /></TabsContent>
        <TabsContent value="subs"><SubsTab data={data} h={h} reload={reload} initialLimitTenant={initialLimitTenant} /></TabsContent>
        <TabsContent value="billing"><BillingTab data={data} h={h} reload={reload} /></TabsContent>
        <TabsContent value="invoices"><InvoicesTab data={data} h={h} reload={reload} /></TabsContent>
        <TabsContent value="discounts"><DiscountsTab data={data} h={h} reload={reload} /></TabsContent>
        <TabsContent value="settings"><SettingsTab key={JSON.stringify(data.settings)} settings={data.settings} h={h} reload={reload} /></TabsContent>
      </Tabs>
    </div>
  );
};

/* ---------------- Plans ---------------- */
const emptyPlan = { plan_code: '', plan_name: '', description: '', monthly_price: 0, yearly_price: 0,
  max_users: 0, max_storage_gb: 0, max_ocr_pages: 0, max_ai_credits: 0, max_workflows: 0, max_api_calls: 0,
  hsn_sac_code: '998314', gst_rate_percent: 18, status: 'active', sort_order: 0 };
const PlansTab = ({ data, h, reload }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyPlan);
  const [editing, setEditing] = useState(null);
  const save = async () => {
    try {
      if (editing) await api.updatePlan(editing, form, h); else await api.createPlan(form, h);
      toast.success('Plan saved'); setOpen(false); setEditing(null); setForm(emptyPlan); reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };
  const toggle = async (p) => { try { await api.togglePlan(p.id, p.status === 'active' ? 'deactivate' : 'activate', h); reload(); } catch { toast.error('Failed'); } };
  const clone = async (p) => { try { await api.clonePlan(p.id, h); toast.success('Cloned'); reload(); } catch { toast.error('Failed'); } };
  return (
    <Card className="mt-4"><CardHeader className="flex flex-row items-center justify-between">
      <CardTitle>Plans</CardTitle>
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(emptyPlan); } }}>
        <DialogTrigger asChild><Button data-testid="add-plan-btn" style={{ backgroundColor: brandColors.primary }}><Plus className="w-4 h-4 mr-1" /> Create Plan</Button></DialogTrigger>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Edit' : 'Create'} Plan</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Plan Code"><Input value={form.plan_code} onChange={(e) => setForm({ ...form, plan_code: e.target.value.toUpperCase() })} data-testid="plan-code" /></Field>
            <Field label="Plan Name"><Input value={form.plan_name} onChange={(e) => setForm({ ...form, plan_name: e.target.value })} data-testid="plan-name" /></Field>
            <div className="col-span-2"><Field label="Description"><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field></div>
            {[['monthly_price', 'Monthly ₹'], ['yearly_price', 'Yearly ₹'], ['max_users', 'Max Users (-1=∞)'], ['max_storage_gb', 'Storage GB'], ['max_ocr_pages', 'OCR Pages'], ['max_ai_credits', 'AI Credits'], ['max_workflows', 'Max Workflows'], ['max_api_calls', 'API Calls'], ['gst_rate_percent', 'GST %'], ['sort_order', 'Sort']].map(([k, l]) => (
              <Field key={k} label={l}><Input type="number" value={form[k]} onChange={(e) => setForm({ ...form, [k]: Number(e.target.value) })} data-testid={`plan-${k}`} /></Field>
            ))}
            <Field label="HSN/SAC"><Input value={form.hsn_sac_code} onChange={(e) => setForm({ ...form, hsn_sac_code: e.target.value })} /></Field>
            <Field label="Status"><Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent></Select></Field>
          </div>
          <DialogFooter><Button onClick={save} data-testid="save-plan-btn" style={{ backgroundColor: brandColors.primary }}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </CardHeader>
      <CardContent>
        <Table data-testid="plans-table"><TableHeader><TableRow>
          <TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead className="text-right">Monthly</TableHead>
          <TableHead className="text-right">Yearly</TableHead><TableHead className="text-right">Users</TableHead>
          <TableHead className="text-right">Credits</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
        </TableRow></TableHeader>
          <TableBody>{data.plans.map((p) => (
            <TableRow key={p.id} data-testid={`plan-row-${p.plan_code}`}>
              <TableCell><Badge variant="outline">{p.plan_code}</Badge></TableCell>
              <TableCell className="font-medium">{p.plan_name}</TableCell>
              <TableCell className="text-right">{fmtINR(p.monthly_price)}</TableCell>
              <TableCell className="text-right">{fmtINR(p.yearly_price)}</TableCell>
              <TableCell className="text-right">{p.max_users < 0 ? '∞' : p.max_users}</TableCell>
              <TableCell className="text-right">{p.max_ai_credits < 0 ? '∞' : p.max_ai_credits}</TableCell>
              <TableCell><Badge className={p.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}>{p.status}</Badge></TableCell>
              <TableCell className="text-right whitespace-nowrap">
                <Button size="sm" variant="ghost" onClick={() => { setEditing(p.id); setForm({ ...emptyPlan, ...p }); setOpen(true); }} data-testid={`edit-plan-${p.plan_code}`}>Edit</Button>
                <Button size="sm" variant="ghost" onClick={() => clone(p)} data-testid={`clone-plan-${p.plan_code}`}><Copy className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" onClick={() => toggle(p)} data-testid={`toggle-plan-${p.plan_code}`}><Power className="w-4 h-4" /></Button>
              </TableCell>
            </TableRow>
          ))}</TableBody></Table>
      </CardContent></Card>
  );
};

/* ---------------- Feature Matrix ---------------- */
const FeaturesTab = ({ data, h, reload }) => {
  // Columns are derived dynamically from the live Plans list so newly created
  // plans automatically get a togglable column (MASTER/internal plan excluded).
  const planCols = (data.plans || [])
    .filter((p) => (p.plan_code || '').toUpperCase() !== 'MASTER')
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
    .map((p) => p.plan_code);
  const toggle = async (f, plan) => {
    const access = { ...(f.plan_access || {}) }; access[plan] = !access[plan];
    try { await api.updateFeature(f.feature_key, { feature_key: f.feature_key, feature_name: f.feature_name, module: f.module, plan_access: access, is_chargeable: f.is_chargeable, credit_cost: f.credit_cost }, h); reload(); }
    catch { toast.error('Update failed'); }
  };
  return (
    <Card className="mt-4"><CardHeader><CardTitle>Feature Matrix</CardTitle></CardHeader>
      <CardContent className="overflow-x-auto">
        <Table data-testid="features-table"><TableHeader><TableRow>
          <TableHead>Feature</TableHead><TableHead>Module</TableHead>
          {planCols.map((c) => <TableHead key={c} className="text-center">{c}</TableHead>)}
          <TableHead className="text-right">Credits</TableHead>
        </TableRow></TableHeader>
          <TableBody>{data.features.map((f) => (
            <TableRow key={f.id || f.feature_key} data-testid={`feature-row-${f.feature_key}`}>
              <TableCell className="font-medium">{f.feature_name}</TableCell>
              <TableCell><span className="text-xs text-slate-400">{f.module}</span></TableCell>
              {planCols.map((c) => (
                <TableCell key={c} className="text-center">
                  <Switch checked={!!(f.plan_access || {})[c]} onCheckedChange={() => toggle(f, c)} data-testid={`feat-${f.feature_key}-${c}`} />
                </TableCell>
              ))}
              <TableCell className="text-right">{f.credit_cost || 0}</TableCell>
            </TableRow>
          ))}</TableBody></Table>
      </CardContent></Card>
  );
};

/* ---------------- AI Credit Master ---------------- */
const CreditMasterTab = ({ data, h, reload }) => {
  const [drafts, setDrafts] = useState({});
  const save = async (a) => {
    const cost = drafts[a.action_key] ?? a.credit_cost;
    try { await api.updateActionCost(a.action_key, { action_key: a.action_key, action_name: a.action_name, module: a.module || a.category, credit_cost: Number(cost), is_active: a.is_active !== false }, h); toast.success('Saved'); reload(); }
    catch { toast.error('Failed'); }
  };
  return (
    <Card className="mt-4"><CardHeader><CardTitle>AI Credit Master</CardTitle></CardHeader>
      <CardContent>
        <Table data-testid="actions-table"><TableHeader><TableRow>
          <TableHead>Action</TableHead><TableHead>Module</TableHead><TableHead className="w-40">Credit Cost</TableHead><TableHead></TableHead>
        </TableRow></TableHeader>
          <TableBody>{data.actions.map((a) => (
            <TableRow key={a.id || a.action_key} data-testid={`action-row-${a.action_key}`}>
              <TableCell className="font-medium">{a.action_name}</TableCell>
              <TableCell><Badge variant="outline">{a.module || a.category}</Badge></TableCell>
              <TableCell><Input type="number" defaultValue={a.credit_cost} onChange={(e) => setDrafts({ ...drafts, [a.action_key]: e.target.value })} data-testid={`action-cost-${a.action_key}`} className="w-28" /></TableCell>
              <TableCell><Button size="sm" variant="outline" onClick={() => save(a)} data-testid={`save-action-${a.action_key}`}><Save className="w-4 h-4" /></Button></TableCell>
            </TableRow>
          ))}</TableBody></Table>
      </CardContent></Card>
  );
};

/* ---------------- OCR Rate Master ---------------- */
const OcrTab = ({ data, h, reload }) => {
  const [drafts, setDrafts] = useState({});
  const save = async (r) => {
    const d = drafts[r.ocr_type] || {};
    try { await api.updateOcrRate(r.ocr_type, { ocr_type: r.ocr_type, rate_per_page: Number(d.rate ?? r.rate_per_page), credits_per_page: Number(d.credits ?? r.credits_per_page), status: r.status || 'active' }, h); toast.success('Saved'); reload(); }
    catch { toast.error('Failed'); }
  };
  return (
    <Card className="mt-4"><CardHeader><CardTitle>OCR Rate Master</CardTitle></CardHeader>
      <CardContent>
        <Table data-testid="ocr-table"><TableHeader><TableRow>
          <TableHead>OCR Type</TableHead><TableHead className="w-40">Rate/Page (₹)</TableHead><TableHead className="w-40">Credits/Page</TableHead><TableHead></TableHead>
        </TableRow></TableHeader>
          <TableBody>{data.ocr.map((r) => (
            <TableRow key={r.ocr_type} data-testid={`ocr-row-${r.ocr_type}`}>
              <TableCell className="font-medium capitalize">{r.ocr_type}</TableCell>
              <TableCell><Input type="number" step="0.01" defaultValue={r.rate_per_page} onChange={(e) => setDrafts({ ...drafts, [r.ocr_type]: { ...drafts[r.ocr_type], rate: e.target.value } })} data-testid={`ocr-rate-${r.ocr_type}`} className="w-28" /></TableCell>
              <TableCell><Input type="number" defaultValue={r.credits_per_page} onChange={(e) => setDrafts({ ...drafts, [r.ocr_type]: { ...drafts[r.ocr_type], credits: e.target.value } })} data-testid={`ocr-credits-${r.ocr_type}`} className="w-28" /></TableCell>
              <TableCell><Button size="sm" variant="outline" onClick={() => save(r)} data-testid={`save-ocr-${r.ocr_type}`}><Save className="w-4 h-4" /></Button></TableCell>
            </TableRow>
          ))}</TableBody></Table>
      </CardContent></Card>
  );
};

/* ---------------- Industry Packs (informational) ---------------- */
const PacksTab = () => (
  <Card className="mt-4"><CardHeader><CardTitle>Industry Packs</CardTitle></CardHeader>
    <CardContent data-testid="packs-info">
      <p className="text-slate-600 text-sm">Industry pack access is governed by the <b>Feature Matrix</b> (<code>industry_packs</code> feature) and bundled into Gold+ plans. Per-pack one-time pricing can be enabled on request.</p>
    </CardContent></Card>
);

/* ---------------- Subscriptions ---------------- */
const SubsTab = ({ data, h, reload, initialLimitTenant }) => {
  const [tenantId, setTenantId] = useState('');
  const [planCode, setPlanCode] = useState('');
  const [cycle, setCycle] = useState('monthly');
  const [topupTenant, setTopupTenant] = useState('');
  const [credits, setCredits] = useState(1000);
  // Manage-limits card state
  const [limTenant, setLimTenant] = useState('');
  const [lim, setLim] = useState(null);
  const [limForm, setLimForm] = useState({ storage_gb: '', max_users: '', add_ai_credits: '', add_ocr_pages: '' });
  const loadLimits = async (tid) => {
    setLimTenant(tid); setLim(null);
    if (!tid) return;
    try {
      const res = await api.getTenantLimits(tid, h);
      setLim(res.data);
      setLimForm({ storage_gb: res.data.storage.allocated_gb, max_users: res.data.users.effective_max, add_ai_credits: '', add_ocr_pages: '' });
    } catch { toast.error('Failed to load tenant limits'); }
  };
  const saveLimits = async () => {
    if (!limTenant) { toast.error('Select tenant'); return; }
    const payload = {};
    if (limForm.storage_gb !== '' && Number(limForm.storage_gb) !== lim?.storage.allocated_gb) payload.storage_gb = Number(limForm.storage_gb);
    if (limForm.max_users !== '' && Number(limForm.max_users) !== lim?.users.effective_max) payload.max_users = Number(limForm.max_users);
    if (limForm.add_ai_credits) payload.add_ai_credits = Number(limForm.add_ai_credits);
    if (limForm.add_ocr_pages) payload.add_ocr_pages = Number(limForm.add_ocr_pages);
    if (Object.keys(payload).length === 0) { toast.info('No changes to apply'); return; }
    try {
      const res = await api.setTenantLimits(limTenant, payload, h);
      setLim(res.data);
      setLimForm({ storage_gb: res.data.storage.allocated_gb, max_users: res.data.users.effective_max, add_ai_credits: '', add_ocr_pages: '' });
      toast.success('Tenant limits updated'); reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update limits'); }
  };
  const assign = async () => {
    if (!tenantId || !planCode) { toast.error('Select tenant and plan'); return; }
    try { await api.assignSubscription({ tenant_id: tenantId, plan_code: planCode, billing_cycle: cycle }, h); toast.success('Assigned'); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const topup = async () => {
    if (!topupTenant) { toast.error('Select tenant'); return; }
    try { await api.topupCredits(topupTenant, { amount: Number(credits) }, h); toast.success('Credits added'); reload(); } catch { toast.error('Failed'); }
  };
  const act = async (s, action) => { try { await api.subscriptionAction(s.id, action, h); toast.success(action); reload(); } catch { toast.error('Failed'); } };
  useEffect(() => {
    if (!initialLimitTenant) return;
    loadLimits(initialLimitTenant);
    const t = setTimeout(() => {
      const el = document.querySelector('[data-testid="manage-limits-card"]');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('ring-2', 'ring-amber-400', 'ring-offset-2', 'rounded-xl');
        setTimeout(() => el.classList.remove('ring-2', 'ring-amber-400', 'ring-offset-2', 'rounded-xl'), 2600);
      }
    }, 500);
    return () => clearTimeout(t);
  }, [initialLimitTenant]);
  return (
    <div className="mt-4 space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card><CardHeader><CardTitle>Assign Plan</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <Field label="Tenant"><Select value={tenantId} onValueChange={setTenantId}><SelectTrigger data-testid="assign-tenant"><SelectValue placeholder="Select tenant" /></SelectTrigger>
              <SelectContent>{data.tenants.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} ({t.code})</SelectItem>)}</SelectContent></Select></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Plan"><Select value={planCode} onValueChange={setPlanCode}><SelectTrigger data-testid="assign-plan"><SelectValue placeholder="Plan" /></SelectTrigger>
                <SelectContent>{data.plans.map((p) => <SelectItem key={p.id} value={p.plan_code}>{p.plan_name}</SelectItem>)}</SelectContent></Select></Field>
              <Field label="Cycle"><Select value={cycle} onValueChange={setCycle}><SelectTrigger data-testid="assign-cycle"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="monthly">Monthly</SelectItem><SelectItem value="yearly">Yearly</SelectItem></SelectContent></Select></Field>
            </div>
            <Button onClick={assign} data-testid="assign-sub-btn" style={{ backgroundColor: brandColors.primary }}>Assign Plan</Button>
          </CardContent></Card>
        <Card><CardHeader><CardTitle>Top Up AI Credits</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <Field label="Tenant"><Select value={topupTenant} onValueChange={setTopupTenant}><SelectTrigger data-testid="topup-tenant"><SelectValue placeholder="Select tenant" /></SelectTrigger>
              <SelectContent>{data.tenants.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} ({t.code})</SelectItem>)}</SelectContent></Select></Field>
            <Field label="Credits"><Input type="number" value={credits} onChange={(e) => setCredits(e.target.value)} data-testid="topup-amount" /></Field>
            <Button onClick={topup} data-testid="topup-btn" style={{ backgroundColor: brandColors.primary }}>Add Credits</Button>
          </CardContent></Card>
      </div>
      <Card data-testid="manage-limits-card"><CardHeader>
        <CardTitle className="flex items-center gap-2"><Gauge className="w-5 h-5 text-slate-500" /> Manage Tenant Limits</CardTitle>
        <p className="text-sm text-slate-500">Increase a tenant's storage, user seats, AI credits or OCR pages when their plan limits are being reached.</p>
      </CardHeader>
        <CardContent className="space-y-4">
          <Field label="Tenant">
            <Select value={limTenant} onValueChange={loadLimits}>
              <SelectTrigger data-testid="limits-tenant"><SelectValue placeholder="Select tenant" /></SelectTrigger>
              <SelectContent>{data.tenants.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} ({t.code})</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          {lim && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm" data-testid="limits-current">
                <LimitStat label="Storage" value={`${lim.storage.used_gb} / ${lim.storage.allocated_gb} GB`} />
                <LimitStat label="Users" value={`${lim.users.active} / ${lim.users.effective_max < 0 ? '∞' : lim.users.effective_max}`} />
                <LimitStat label="AI Credits" value={Number(lim.ai_credits.available).toLocaleString()} />
                <LimitStat label="OCR Pages" value={Number(lim.ocr_pages.available).toLocaleString()} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <Field label="Storage (GB) — set total"><Input type="number" value={limForm.storage_gb} onChange={(e) => setLimForm({ ...limForm, storage_gb: e.target.value })} data-testid="limits-storage-gb" /></Field>
                <Field label="Max Users — set total (-1 = ∞)"><Input type="number" value={limForm.max_users} onChange={(e) => setLimForm({ ...limForm, max_users: e.target.value })} data-testid="limits-max-users" /></Field>
                <Field label="Add AI Credits"><Input type="number" placeholder="0" value={limForm.add_ai_credits} onChange={(e) => setLimForm({ ...limForm, add_ai_credits: e.target.value })} data-testid="limits-add-credits" /></Field>
                <Field label="Add OCR Pages"><Input type="number" placeholder="0" value={limForm.add_ocr_pages} onChange={(e) => setLimForm({ ...limForm, add_ocr_pages: e.target.value })} data-testid="limits-add-ocr" /></Field>
              </div>
              <Button onClick={saveLimits} data-testid="save-limits-btn" style={{ backgroundColor: brandColors.primary }}><Save className="w-4 h-4 mr-1" /> Apply Changes</Button>
            </>
          )}
        </CardContent></Card>
      <Card><CardHeader><CardTitle>Subscriptions</CardTitle></CardHeader>
        <CardContent>
          {data.subs.length === 0 ? <p className="text-slate-500 text-sm">No subscriptions.</p> : (
            <Table data-testid="subs-table"><TableHeader><TableRow>
              <TableHead>Tenant</TableHead><TableHead>Plan</TableHead><TableHead>Cycle</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
              <TableBody>{data.subs.map((s) => (
                <TableRow key={s.id} data-testid={`sub-row-${s.id}`}>
                  <TableCell className="font-medium">{s.tenant_name}</TableCell>
                  <TableCell><Badge variant="outline">{s.plan_code}</Badge></TableCell>
                  <TableCell className="capitalize">{s.billing_cycle}</TableCell>
                  <TableCell className="text-right">{fmtINR(s.final_price || s.amount)}</TableCell>
                  <TableCell><Badge className={s.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}>{s.status}</Badge></TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => act(s, 'renew')} data-testid={`renew-${s.id}`}>Renew</Button>
                    <Button size="sm" variant="ghost" onClick={() => act(s, 'suspend')} data-testid={`suspend-${s.id}`}>Suspend</Button>
                  </TableCell>
                </TableRow>
              ))}</TableBody></Table>
          )}
        </CardContent></Card>
    </div>
  );
};

/* ---------------- Billing ---------------- */
const BillingTab = ({ data, h, reload }) => {
  const [tenantId, setTenantId] = useState('');
  const generate = async () => {
    if (!tenantId) { toast.error('Select tenant'); return; }
    const now = new Date(); const start = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString();
    try { await api.generateBilling(tenantId, { period_start: start, period_end: end }, h); toast.success('Billing generated'); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  return (
    <Card className="mt-4"><CardHeader className="flex flex-row items-center justify-between">
      <CardTitle>Billing Records</CardTitle>
      <div className="flex gap-2 items-end">
        <Select value={tenantId} onValueChange={setTenantId}><SelectTrigger className="w-56" data-testid="billing-tenant"><SelectValue placeholder="Tenant" /></SelectTrigger>
          <SelectContent>{data.tenants.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} ({t.code})</SelectItem>)}</SelectContent></Select>
        <Button onClick={generate} data-testid="generate-billing-btn" style={{ backgroundColor: brandColors.primary }}>Generate</Button>
      </div>
    </CardHeader>
      <CardContent>
        {data.billing.length === 0 ? <p className="text-slate-500 text-sm">No billing records yet.</p> : (
          <Table data-testid="billing-table"><TableHeader><TableRow>
            <TableHead>Period</TableHead><TableHead className="text-right">Subtotal</TableHead><TableHead className="text-right">Tax</TableHead><TableHead className="text-right">Total</TableHead><TableHead>Status</TableHead>
          </TableRow></TableHeader>
            <TableBody>{data.billing.map((b) => (
              <TableRow key={b.id}>
                <TableCell>{String(b.billing_period_start).slice(0, 10)} → {String(b.billing_period_end).slice(0, 10)}</TableCell>
                <TableCell className="text-right">{fmtINR(b.subtotal)}</TableCell>
                <TableCell className="text-right">{fmtINR(b.total_tax)}</TableCell>
                <TableCell className="text-right font-semibold">{fmtINR(b.total_amount)}</TableCell>
                <TableCell><Badge variant="outline">{b.status}</Badge></TableCell>
              </TableRow>
            ))}</TableBody></Table>
        )}
      </CardContent></Card>
  );
};

/* ---------------- Invoices ---------------- */
const InvoicesTab = ({ data, h, reload }) => {
  const [open, setOpen] = useState(false);
  const [tenantId, setTenantId] = useState('');
  const [desc, setDesc] = useState('Subscription charge');
  const [qty, setQty] = useState(1);
  const [price, setPrice] = useState(12999);
  const [state, setState] = useState('');
  const create = async () => {
    if (!tenantId) { toast.error('Select tenant'); return; }
    try { await api.createInvoiceReq({ tenant_id: tenantId, line_items: [{ description: desc, quantity: Number(qty), unit_price: Number(price) }], buyer: state ? { state } : {} }, h); toast.success('Invoice created'); setOpen(false); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const bulk = async () => { try { const r = await api.bulkGenerateInvoices(h); toast.success(`Generated ${r.data.generated} invoices`); reload(); } catch { toast.error('Failed'); } };
  const markPaid = async (inv) => { try { await api.markInvoicePaid(inv.id, { payment_method: 'Bank Transfer / NEFT', payment_reference: 'MANUAL' }, h); toast.success('Marked paid'); reload(); } catch { toast.error('Failed'); } };
  const dl = async (inv, receipt) => {
    try {
      const res = receipt ? await api.downloadReceipt(inv.id, h) : await api.downloadInvoice(inv.id, h);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url;
      a.download = `${((receipt ? inv.receipt_number : inv.invoice_number) || 'doc').replace(/\//g, '-')}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };
  return (
    <Card className="mt-4"><CardHeader className="flex flex-row items-center justify-between">
      <CardTitle>Invoices</CardTitle>
      <div className="flex gap-2">
        <Button variant="outline" onClick={bulk} data-testid="bulk-invoice-btn">Generate All (Active)</Button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-invoice-btn" style={{ backgroundColor: brandColors.primary }}><Plus className="w-4 h-4 mr-1" /> New Invoice</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create GST Invoice</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Field label="Tenant"><Select value={tenantId} onValueChange={setTenantId}><SelectTrigger data-testid="inv-tenant"><SelectValue placeholder="Select tenant" /></SelectTrigger>
                <SelectContent>{data.tenants.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} ({t.code})</SelectItem>)}</SelectContent></Select></Field>
              <Field label="Description"><Input value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="inv-desc" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Quantity"><Input type="number" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="inv-qty" /></Field>
                <Field label="Unit Price ₹"><Input type="number" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="inv-price" /></Field>
              </div>
              <Field label="Buyer State (blank = intra-state CGST+SGST)"><Select value={state} onValueChange={setState}><SelectTrigger data-testid="inv-state"><SelectValue placeholder="Same as seller" /></SelectTrigger>
                <SelectContent>{INDIAN_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></Field>
            </div>
            <DialogFooter><Button onClick={create} data-testid="create-invoice-btn" style={{ backgroundColor: brandColors.primary }}>Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </CardHeader>
      <CardContent>
        {data.invoices.length === 0 ? <p className="text-slate-500 text-sm">No invoices yet.</p> : (
          <Table data-testid="all-invoices-table"><TableHeader><TableRow>
            <TableHead>Invoice #</TableHead><TableHead>Date</TableHead><TableHead className="text-right">CGST</TableHead>
            <TableHead className="text-right">SGST</TableHead><TableHead className="text-right">IGST</TableHead>
            <TableHead className="text-right">Total</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
            <TableBody>{data.invoices.map((inv) => (
              <TableRow key={inv.id} data-testid={`rev-invoice-${inv.id}`}>
                <TableCell className="font-medium">{inv.invoice_number}</TableCell>
                <TableCell>{String(inv.invoice_date || inv.issue_date).slice(0, 10)}</TableCell>
                <TableCell className="text-right">{fmtINR(inv.cgst_amount)}</TableCell>
                <TableCell className="text-right">{fmtINR(inv.sgst_amount)}</TableCell>
                <TableCell className="text-right">{fmtINR(inv.igst_amount)}</TableCell>
                <TableCell className="text-right font-semibold">{fmtINR(inv.total_amount || inv.grand_total)}</TableCell>
                <TableCell><Badge className={inv.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}>{inv.payment_status || inv.status}</Badge></TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={() => dl(inv, false)} data-testid={`dl-invoice-${inv.id}`} title="Download invoice"><Download className="w-4 h-4" /></Button>
                  {inv.payment_status === 'paid'
                    ? <Button size="sm" variant="ghost" onClick={() => dl(inv, true)} data-testid={`dl-receipt-${inv.id}`} title="Download receipt"><Receipt className="w-4 h-4 text-emerald-600" /></Button>
                    : <Button size="sm" variant="ghost" onClick={() => markPaid(inv)} data-testid={`markpaid-${inv.id}`} title="Mark paid"><FileText className="w-4 h-4 text-amber-600" /></Button>}
                </TableCell>
              </TableRow>
            ))}</TableBody></Table>
        )}
      </CardContent></Card>
  );
};

/* ---------------- Discounts ---------------- */
const emptyDiscount = { code: '', description: '', discount_type: 'percentage', discount_value: 10, applicable_to: 'all', min_amount: null, max_uses: null, is_active: true };
const DiscountsTab = ({ data, h, reload }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyDiscount);
  const save = async () => { try { await api.createDiscount(form, h); toast.success('Discount created'); setOpen(false); setForm(emptyDiscount); reload(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } };
  const deactivate = async (d) => { try { await api.deactivateDiscount(d.id, h); reload(); } catch { toast.error('Failed'); } };
  return (
    <Card className="mt-4"><CardHeader className="flex flex-row items-center justify-between">
      <CardTitle>Discounts</CardTitle>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild><Button data-testid="add-discount-btn" style={{ backgroundColor: brandColors.primary }}><Plus className="w-4 h-4 mr-1" /> Create Code</Button></DialogTrigger>
        <DialogContent>
          <DialogHeader><DialogTitle>Create Discount</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Field label="Code"><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} data-testid="discount-code" /></Field>
            <Field label="Description"><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Type"><Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })}><SelectTrigger data-testid="discount-type"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="percentage">Percentage</SelectItem><SelectItem value="flat">Flat</SelectItem></SelectContent></Select></Field>
              <Field label="Value"><Input type="number" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: Number(e.target.value) })} data-testid="discount-value" /></Field>
            </div>
            <Field label="Applicable To"><Select value={form.applicable_to} onValueChange={(v) => setForm({ ...form, applicable_to: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="subscription">Subscription</SelectItem><SelectItem value="pack">Pack</SelectItem><SelectItem value="credits">Credits</SelectItem></SelectContent></Select></Field>
          </div>
          <DialogFooter><Button onClick={save} data-testid="save-discount-btn" style={{ backgroundColor: brandColors.primary }}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </CardHeader>
      <CardContent>
        {data.discounts.length === 0 ? <p className="text-slate-500 text-sm">No discount codes.</p> : (
          <Table data-testid="discounts-table"><TableHeader><TableRow>
            <TableHead>Code</TableHead><TableHead>Type</TableHead><TableHead className="text-right">Value</TableHead><TableHead>Applicable</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
            <TableBody>{data.discounts.map((d) => (
              <TableRow key={d.id} data-testid={`discount-row-${d.code}`}>
                <TableCell className="font-medium">{d.code}</TableCell>
                <TableCell className="capitalize">{d.discount_type}</TableCell>
                <TableCell className="text-right">{d.discount_type === 'percentage' ? `${d.discount_value}%` : fmtINR(d.discount_value)}</TableCell>
                <TableCell className="capitalize">{d.applicable_to}</TableCell>
                <TableCell><Badge className={d.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}>{d.is_active ? 'active' : 'inactive'}</Badge></TableCell>
                <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={() => deactivate(d)} data-testid={`deactivate-discount-${d.code}`}><Trash2 className="w-4 h-4 text-red-500" /></Button></TableCell>
              </TableRow>
            ))}</TableBody></Table>
        )}
      </CardContent></Card>
  );
};

/* ---------------- System Settings ---------------- */
const SettingsTab = ({ settings, h, reload }) => {
  const [seller, setSeller] = useState(settings.seller_details || {});
  const [bank, setBank] = useState(settings.bank_details || {});
  const [inv, setInv] = useState(settings.invoice_config || {});
  const [mode, setMode] = useState(settings.enforcement_mode || 'track');
  const [storageMode, setStorageMode] = useState(settings.storage_enforcement_mode || 'track');
  const [rzp, setRzp] = useState({
    key_id: settings.razorpay_config?.key_id || '',
    key_secret: settings.razorpay_config?.has_secret ? '********' : '',
    mode: settings.razorpay_config?.mode || 'demo',
  });
  const saveAll = async () => {
    try { await api.saveRevenueSettings({ seller_details: seller, bank_details: bank, invoice_config: inv, enforcement_mode: mode, storage_enforcement_mode: storageMode, razorpay_config: rzp }, h); toast.success('Settings saved'); reload(); }
    catch { toast.error('Failed'); }
  };
  const fld = (state, setState, k, l, type = 'text') => (
    <Field label={l}><Input type={type} value={state[k] ?? ''} onChange={(e) => setState({ ...state, [k]: type === 'number' ? Number(e.target.value) : e.target.value })} data-testid={`set-${k}`} /></Field>
  );
  return (
    <div className="mt-4 space-y-6">
      <Card><CardHeader><CardTitle>Seller Details</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {fld(seller, setSeller, 'company_name', 'Company Name')}{fld(seller, setSeller, 'gstin', 'GSTIN')}{fld(seller, setSeller, 'pan', 'PAN')}
          {fld(seller, setSeller, 'address', 'Address')}{fld(seller, setSeller, 'city', 'City')}
          <Field label="State"><Select value={seller.state || ''} onValueChange={(v) => setSeller({ ...seller, state: v })}><SelectTrigger data-testid="set-state"><SelectValue placeholder="State" /></SelectTrigger><SelectContent>{INDIAN_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></Field>
          {fld(seller, setSeller, 'state_code', 'State Code')}{fld(seller, setSeller, 'pincode', 'PIN')}{fld(seller, setSeller, 'email', 'Email')}{fld(seller, setSeller, 'phone', 'Phone')}
        </CardContent></Card>
      <Card><CardHeader><CardTitle>Bank Details</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {fld(bank, setBank, 'bank_name', 'Bank Name')}{fld(bank, setBank, 'bank_account', 'Account Number')}{fld(bank, setBank, 'ifsc_code', 'IFSC')}
          {fld(bank, setBank, 'account_name', 'Account Name')}{fld(bank, setBank, 'upi_id', 'UPI ID')}
        </CardContent></Card>
      <Card><CardHeader><CardTitle>Invoice &amp; GST Configuration</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {fld(inv, setInv, 'payment_terms', 'Payment Terms')}{fld(inv, setInv, 'gst_default_rate', 'GST Default %', 'number')}{fld(inv, setInv, 'currency', 'Currency')}
          {fld(inv, setInv, 'cgst_rate', 'CGST %', 'number')}{fld(inv, setInv, 'sgst_rate', 'SGST %', 'number')}{fld(inv, setInv, 'igst_rate', 'IGST %', 'number')}
          <div className="md:col-span-3"><Field label="Footer Note"><Textarea value={inv.footer_note || ''} onChange={(e) => setInv({ ...inv, footer_note: e.target.value })} /></Field></div>
        </CardContent></Card>
      <Card><CardHeader><CardTitle>Enforcement Modes</CardTitle></CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-2">
            <p className="text-sm font-medium text-slate-700">AI Credit Enforcement</p>
            <Select value={mode} onValueChange={setMode}><SelectTrigger className="w-72" data-testid="enforcement-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="track">Track (meter only, never block)</SelectItem><SelectItem value="warn">Warn (notify on low balance)</SelectItem><SelectItem value="enforce">Enforce (block when empty)</SelectItem></SelectContent></Select>
            {mode === 'enforce' && (
              <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-amber-800 text-sm">
                <AlertTriangle className="w-5 h-5 mt-0.5" /> &apos;Enforce&apos; will block AI features for tenants with zero credits. Ensure all tenants have credit wallets before switching.
              </div>
            )}
          </div>
          <div className="space-y-2 border-t border-slate-100 pt-4">
            <p className="text-sm font-medium text-slate-700">Storage Quota Enforcement</p>
            <Select value={storageMode} onValueChange={setStorageMode}><SelectTrigger className="w-72" data-testid="storage-enforcement-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="track">Track (meter only, never block)</SelectItem><SelectItem value="warn">Warn (notify on over-limit)</SelectItem><SelectItem value="enforce">Enforce (block uploads over quota)</SelectItem></SelectContent></Select>
            {storageMode === 'enforce' && (
              <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-amber-800 text-sm">
                <AlertTriangle className="w-5 h-5 mt-0.5" /> &apos;Enforce&apos; will block uploads for tenants that exceed their plan storage. Verify plan allocations first.
              </div>
            )}
          </div>
        </CardContent></Card>
      <Card><CardHeader><CardTitle>Razorpay Payment Gateway</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start gap-2 rounded-lg bg-blue-50 border border-blue-200 p-3 text-blue-800 text-sm">
            <AlertTriangle className="w-5 h-5 mt-0.5" /> The Razorpay checkout currently runs in <strong>&nbsp;Demo mode&nbsp;</strong> (no real charges). Enter your live API keys below and switch mode to <strong>&nbsp;Live&nbsp;</strong> to enable real payments in the future.
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Key ID"><Input value={rzp.key_id} onChange={(e) => setRzp({ ...rzp, key_id: e.target.value })} placeholder="rzp_live_xxxxxxxx" data-testid="razorpay-key-id" /></Field>
            <Field label="Key Secret"><Input type="password" value={rzp.key_secret} onChange={(e) => setRzp({ ...rzp, key_secret: e.target.value })} placeholder="Enter secret" data-testid="razorpay-key-secret" /></Field>
            <Field label="Mode"><Select value={rzp.mode} onValueChange={(v) => setRzp({ ...rzp, mode: v })}><SelectTrigger data-testid="razorpay-mode"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="demo">Demo (mock, no charge)</SelectItem><SelectItem value="live">Live (real payments)</SelectItem></SelectContent></Select></Field>
          </div>
        </CardContent></Card>
      <Button onClick={saveAll} data-testid="save-settings-btn" style={{ backgroundColor: brandColors.primary }}><Save className="w-4 h-4 mr-1" /> Save All Settings</Button>
    </div>
  );
};

export default RevenueAdmin;
